import { setAuthToken } from 'core/Api';
import { buildLocStoreKeys } from 'store/types';
import { PaidLeaveDaysByYear } from './helpers/leaves';

interface IResProps {
  data: {
    access_token: string;
    refresh_token: string | null;
  };
}

interface IUserInfoProps {
  id: number;
  paidLeaveDays: number;
  paidLeaveDaysByYear?: PaidLeaveDaysByYear[];
  paidLeaveDaysServedTime?: number;
  paidLeaveDaysIrregularWorkingHours?: number;
  paidLeaveDaysSpecificWorkingConditions?: number;
  paidLeaveDaysAdditionalLeave?: number;
  name: string;
  email: string;
  wfhLimitType: string;
  remainingWfhLeavesPerWeek: number;
  remainingWfhLeavesPerMonth: number;
  featureLeavesIsActive: boolean;
  featureLeaveReview: boolean;
  featureLeavesHalfDayOption: boolean;
  featureForm76: boolean;
  featureColleaguesLeaves: boolean;
  featureExitInterview: boolean;
  featureSickLeaveWithoutAttachment: boolean;
  featureArt157LeaveWithoutAttachment: boolean;
  featureLeaveDaysServedTime: boolean;
  featureLeaveDaysIrregularWorkingHours: boolean;
  featureLeaveDaysSpecificWorkingConditions: boolean;
  featureLeaveDaysAdditionalLeave: boolean;
  featureHourlyAbsences: boolean;
  featureHealthAndSafetyTraining: boolean;
  featureWorkTrips: boolean;
  featureCustomFeatureForClientF: boolean;
  featureLimitWfhLeaveDays: boolean;
  featureShowWFHLeaveTypeInMainDropdown: boolean;
  featureLeaveSubstituteEmployee: boolean;
  featureGroupRoleLimitsIsActive: boolean;
  featureLimitDocumentsToLeaves: boolean;
  reminderForIdCardExpiration: boolean;
  featureRequireLeaveSubstituteEmployee: boolean;
  featureEmployeeCandidatesIsActive: boolean;
  featureReminderForProbationPeriodEnd: boolean;
  shouldDisallowLeaveDays: boolean;
  disallowedLeavesFrom: string;
  disallowedLeavesTo: string;
  isCandidate: boolean;
  employeeCandidateHelpText: string;
  totalHourlyAbsencesRemainingHours?: number;
  totalHourlyAbsencesUncoveredHours?: number;
  totalHourlyAbsencesHoursAboutToBeCovered?: number;
  hasAvailableArchives?: boolean;
  availableUndownloadedArchivesCount?: number;
  inProgressArchivesCount?: number;
  lastArchiveExpiresAt?: string | null;
}

const getLocStoreKeys = () => buildLocStoreKeys();

export const setAuthTokensFromResponse = (res: IResProps): Promise<void> => {
  const LocStoreKeys = getLocStoreKeys();
  return new Promise(resolve => {
    const { access_token: accessToken, refresh_token: refreshToken } = res.data;

    // Set tokens in storage
    if (accessToken) {
      localStorage.setItem(LocStoreKeys.ACCESS_TOKEN, accessToken);

      // Set API Auth header
      setAuthToken(accessToken);
    }
    if (refreshToken) {
      localStorage.setItem(LocStoreKeys.REFRESH_TOKEN, refreshToken);
    }
    resolve();
  });
};

export const setUserInfo = ({
  id,
  name,
  email,
  paidLeaveDays,
  paidLeaveDaysByYear,
  paidLeaveDaysServedTime,
  paidLeaveDaysIrregularWorkingHours,
  paidLeaveDaysSpecificWorkingConditions,
  paidLeaveDaysAdditionalLeave,
  wfhLimitType,
  remainingWfhLeavesPerWeek,
  remainingWfhLeavesPerMonth,
  featureLeavesIsActive,
  featureLeaveReview,
  featureLeavesHalfDayOption,
  featureForm76,
  featureColleaguesLeaves,
  featureExitInterview,
  featureSickLeaveWithoutAttachment,
  featureArt157LeaveWithoutAttachment,
  featureLeaveDaysServedTime,
  featureLeaveDaysIrregularWorkingHours,
  featureLeaveDaysSpecificWorkingConditions,
  featureLeaveDaysAdditionalLeave,
  featureHourlyAbsences,
  featureHealthAndSafetyTraining,
  featureWorkTrips,
  featureCustomFeatureForClientF,
  featureLimitWfhLeaveDays,
  featureShowWFHLeaveTypeInMainDropdown,
  featureLeaveSubstituteEmployee,
  featureRequireLeaveSubstituteEmployee,
  featureEmployeeCandidatesIsActive,
  featureGroupRoleLimitsIsActive,
  featureReminderForProbationPeriodEnd,
  featureLimitDocumentsToLeaves,
  reminderForIdCardExpiration,
  shouldDisallowLeaveDays,
  disallowedLeavesFrom,
  disallowedLeavesTo,
  isCandidate,
  employeeCandidateHelpText,
  totalHourlyAbsencesRemainingHours,
  totalHourlyAbsencesUncoveredHours,
  totalHourlyAbsencesHoursAboutToBeCovered,
  hasAvailableArchives,
  availableUndownloadedArchivesCount,
  inProgressArchivesCount,
  lastArchiveExpiresAt,
}: IUserInfoProps): void => {
  const LocStoreKeys = getLocStoreKeys();

  localStorage.setItem(LocStoreKeys.USER_ID, id.toString());
  if (paidLeaveDays !== undefined) localStorage.setItem(LocStoreKeys.PAID_LEAVE_DAYS, paidLeaveDays?.toString());
  if (paidLeaveDaysByYear)
    localStorage.setItem(LocStoreKeys.PAID_LEAVE_DAYS_BY_YEAR, JSON.stringify(paidLeaveDaysByYear));
  if (paidLeaveDaysServedTime !== undefined)
    localStorage.setItem(LocStoreKeys.PAID_LEAVE_DAYS_SERVED_TIME, paidLeaveDaysServedTime?.toString());
  if (paidLeaveDaysIrregularWorkingHours !== undefined)
    localStorage.setItem(
      LocStoreKeys.PAID_LEAVE_DAYS_IRREGULAR_WORKING_HOURS,
      paidLeaveDaysIrregularWorkingHours?.toString()
    );
  if (paidLeaveDaysSpecificWorkingConditions !== undefined)
    localStorage.setItem(
      LocStoreKeys.PAID_LEAVE_DAYS_SPECIFIC_WORKING_CONDITIONS,
      paidLeaveDaysSpecificWorkingConditions?.toString()
    );
  if (paidLeaveDaysAdditionalLeave !== undefined)
    localStorage.setItem(LocStoreKeys.PAID_LEAVE_DAYS_ADDITIONAL_LEAVE, paidLeaveDaysAdditionalLeave?.toString());
  localStorage.setItem(LocStoreKeys.USER_NAME, name);
  localStorage.setItem(LocStoreKeys.USER_EMAIL, email);
  localStorage.setItem(LocStoreKeys.WFH_LIMIT_TYPE, wfhLimitType?.toString());
  localStorage.setItem(LocStoreKeys.REMAINING_WFH_LEAVES_PER_WEEK, remainingWfhLeavesPerWeek?.toString());
  localStorage.setItem(LocStoreKeys.REMAINING_WFH_LEAVES_PER_MONTH, remainingWfhLeavesPerMonth?.toString());
  localStorage.setItem(LocStoreKeys.FEATURE_LEAVES, featureLeavesIsActive.toString());
  localStorage.setItem(LocStoreKeys.FEATURE_LEAVE_REVIEW, featureLeaveReview?.toString());
  localStorage.setItem(LocStoreKeys.FEATURE_LEAVES_HALF_DAY, featureLeavesHalfDayOption?.toString());
  localStorage.setItem(LocStoreKeys.FEATURE_FORM_76, featureForm76.toString());
  localStorage.setItem(LocStoreKeys.FEATURE_COLLEAGUES_LEAVES, featureColleaguesLeaves.toString());
  localStorage.setItem(LocStoreKeys.FEATURE_EXIT_INTERVIEW, featureExitInterview.toString());
  localStorage.setItem(
    LocStoreKeys.FEATURE_SICK_LEAVE_WITHOUT_ATTACHMENT,
    featureSickLeaveWithoutAttachment.toString()
  );
  localStorage.setItem(
    LocStoreKeys.FEATURE_ART157_LEAVE_WITHOUT_ATTACHMENT,
    featureArt157LeaveWithoutAttachment.toString()
  );
  localStorage.setItem(LocStoreKeys.FEATURE_LEAVE_DAYS_SERVED_TIME, featureLeaveDaysServedTime.toString());
  localStorage.setItem(
    LocStoreKeys.FEATURE_LEAVE_DAYS_IRREGULAR_WORKING_HOURS,
    featureLeaveDaysIrregularWorkingHours?.toString()
  );
  localStorage.setItem(
    LocStoreKeys.FEATURE_LEAVE_DAYS_SPECIFIC_WORKING_CONDITIONS,
    featureLeaveDaysSpecificWorkingConditions?.toString()
  );
  localStorage.setItem(LocStoreKeys.FEATURE_LEAVE_DAYS_ADDITIONAL_LEAVE, featureLeaveDaysAdditionalLeave.toString());
  localStorage.setItem(LocStoreKeys.FEATURE_HOURLY_ABSENCES, featureHourlyAbsences?.toString());
  localStorage.setItem(LocStoreKeys.FEATURE_HEALTH_AND_SAFETY_TRAINING, featureHealthAndSafetyTraining?.toString());
  localStorage.setItem(LocStoreKeys.FEATURE_WORK_TRIPS, featureWorkTrips?.toString());
  localStorage.setItem(LocStoreKeys.FEATURE_CUSTOM_FEATURE_FOR_CLIENT_F, featureCustomFeatureForClientF?.toString());
  localStorage.setItem(LocStoreKeys.FEATURE_LIMIT_WFH_LEAVE_DAYS, featureLimitWfhLeaveDays?.toString());
  localStorage.setItem(
    LocStoreKeys.FEATURE_SHOW_WFH_LEAVE_TYPE_IN_MAIN_DROPDOWN,
    featureShowWFHLeaveTypeInMainDropdown?.toString()
  );
  localStorage.setItem(LocStoreKeys.FEATURE_GROUP_ROLE_LIMITS_IS_ACTIVE, featureGroupRoleLimitsIsActive?.toString());
  localStorage.setItem(
    LocStoreKeys.FEATURE_REMINDER_FOR_PROBATION_PERIOD_END,
    featureReminderForProbationPeriodEnd?.toString()
  );
  localStorage.setItem(LocStoreKeys.FEATURE_LIMIT_DOCUMENTS_TO_LEAVES, featureLimitDocumentsToLeaves?.toString());
  localStorage.setItem(LocStoreKeys.REMINDER_FOR_ID_CARD_EXPIRATION, reminderForIdCardExpiration?.toString());
  localStorage.setItem(LocStoreKeys.SHOULD_DISALLOW_LEAVE_DAYS, shouldDisallowLeaveDays?.toString());
  localStorage.setItem(LocStoreKeys.DISALLOWED_LEAVES_FROM, disallowedLeavesFrom || '');
  localStorage.setItem(LocStoreKeys.DISALLOWED_LEAVES_TO, disallowedLeavesTo || '');
  localStorage.setItem(LocStoreKeys.FEATURE_LEAVE_SUBSTITUTE_EMPLOYEE, featureLeaveSubstituteEmployee.toString());
  localStorage.setItem(
    LocStoreKeys.FEATURE_REQUIRE_LEAVE_SUBSTITUTE_EMPLOYEE,
    featureRequireLeaveSubstituteEmployee.toString()
  );
  localStorage.setItem(
    LocStoreKeys.FEATURE_EMPLOYEE_CANDIDATES_IS_ACTIVE,
    featureEmployeeCandidatesIsActive.toString()
  );
  localStorage.setItem(LocStoreKeys.IS_CANDIDATE, isCandidate?.toString());
  localStorage.setItem(LocStoreKeys.EMPLOYEE_CANDIDATE_HELP_TEXT, employeeCandidateHelpText || '');
  if (totalHourlyAbsencesRemainingHours !== undefined)
    localStorage.setItem(
      LocStoreKeys.TOTAL_HOURLY_ABSENCES_REMAINING_HOURS,
      totalHourlyAbsencesRemainingHours?.toString()
    );
  if (totalHourlyAbsencesUncoveredHours !== undefined)
    localStorage.setItem(
      LocStoreKeys.TOTAL_HOURLY_ABSENCES_UNCOVERED_HOURS,
      totalHourlyAbsencesUncoveredHours?.toString()
    );
  if (totalHourlyAbsencesHoursAboutToBeCovered !== undefined)
    localStorage.setItem(
      LocStoreKeys.TOTAL_HOURLY_ABSENCES_HOURS_ABOUT_TO_BE_COVERED,
      totalHourlyAbsencesHoursAboutToBeCovered?.toString()
    );
  if (hasAvailableArchives !== undefined)
    localStorage.setItem(LocStoreKeys.HAS_AVAILABLE_ARCHIVES, hasAvailableArchives.toString());
  if (availableUndownloadedArchivesCount !== undefined)
    localStorage.setItem(
      LocStoreKeys.AVAILABLE_UNDOWNLOADED_ARCHIVES_COUNT,
      availableUndownloadedArchivesCount.toString()
    );
  if (inProgressArchivesCount !== undefined)
    localStorage.setItem(LocStoreKeys.IN_PROGRESS_ARCHIVES_COUNT, inProgressArchivesCount.toString());
  if (lastArchiveExpiresAt !== undefined)
    localStorage.setItem(LocStoreKeys.LAST_ARCHIVE_EXPIRES_AT, lastArchiveExpiresAt || '');
};

export const getUserInfo = (): IUserInfoProps => {
  const LocStoreKeys = getLocStoreKeys();

  const id = localStorage.getItem(LocStoreKeys.USER_ID) || '';
  const paidLeaveDays = localStorage.getItem(LocStoreKeys.PAID_LEAVE_DAYS) || '';
  const paidLeaveDaysByYear = localStorage.getItem(LocStoreKeys.PAID_LEAVE_DAYS_BY_YEAR) || '[]';
  const paidLeaveDaysServedTime = localStorage.getItem(LocStoreKeys.PAID_LEAVE_DAYS_SERVED_TIME) || '';
  const paidLeaveDaysIrregularWorkingHours =
    localStorage.getItem(LocStoreKeys.PAID_LEAVE_DAYS_IRREGULAR_WORKING_HOURS) || '';
  const paidLeaveDaysSpecificWorkingConditions =
    localStorage.getItem(LocStoreKeys.PAID_LEAVE_DAYS_SPECIFIC_WORKING_CONDITIONS) || '';
  const paidLeaveDaysAdditionalLeave = localStorage.getItem(LocStoreKeys.PAID_LEAVE_DAYS_ADDITIONAL_LEAVE) || '';
  const name = localStorage.getItem(LocStoreKeys.USER_NAME) || '';
  const email = localStorage.getItem(LocStoreKeys.USER_EMAIL) || '';
  const wfhLimitType = localStorage.getItem(LocStoreKeys.WFH_LIMIT_TYPE) || '';
  const remainingWfhLeavesPerWeek = localStorage.getItem(LocStoreKeys.REMAINING_WFH_LEAVES_PER_WEEK) || '';
  const remainingWfhLeavesPerMonth = localStorage.getItem(LocStoreKeys.REMAINING_WFH_LEAVES_PER_MONTH) || '';
  const featureLeavesIsActive = localStorage.getItem(LocStoreKeys.FEATURE_LEAVES) === 'true' || false;
  const featureLeavesHalfDayOption = localStorage.getItem(LocStoreKeys.FEATURE_LEAVES_HALF_DAY) === 'true' || false;
  const featureLeaveReview = localStorage.getItem(LocStoreKeys.FEATURE_LEAVE_REVIEW) === 'true' || false;
  const featureForm76 = localStorage.getItem(LocStoreKeys.FEATURE_FORM_76) === 'true' || false;
  const featureColleaguesLeaves = localStorage.getItem(LocStoreKeys.FEATURE_COLLEAGUES_LEAVES) === 'true' || false;
  const featureExitInterview = localStorage.getItem(LocStoreKeys.FEATURE_EXIT_INTERVIEW) === 'true' || false;
  const featureSickLeaveWithoutAttachment =
    localStorage.getItem(LocStoreKeys.FEATURE_SICK_LEAVE_WITHOUT_ATTACHMENT) === 'true' || false;
  const featureArt157LeaveWithoutAttachment =
    localStorage.getItem(LocStoreKeys.FEATURE_ART157_LEAVE_WITHOUT_ATTACHMENT) === 'true' || false;
  const featureLeaveDaysServedTime =
    localStorage.getItem(LocStoreKeys.FEATURE_LEAVE_DAYS_SERVED_TIME) === 'true' || false;
  const featureLeaveDaysIrregularWorkingHours =
    localStorage.getItem(LocStoreKeys.FEATURE_LEAVE_DAYS_IRREGULAR_WORKING_HOURS) === 'true' || false;
  const featureLeaveDaysSpecificWorkingConditions =
    localStorage.getItem(LocStoreKeys.FEATURE_LEAVE_DAYS_SPECIFIC_WORKING_CONDITIONS) === 'true' || false;
  const featureLeaveDaysAdditionalLeave =
    localStorage.getItem(LocStoreKeys.FEATURE_LEAVE_DAYS_ADDITIONAL_LEAVE) === 'true' || false;
  const featureHourlyAbsences = localStorage.getItem(LocStoreKeys.FEATURE_HOURLY_ABSENCES) === 'true' || false;
  const featureHealthAndSafetyTraining =
    localStorage.getItem(LocStoreKeys.FEATURE_HEALTH_AND_SAFETY_TRAINING) === 'true' || false;
  const featureWorkTrips = localStorage.getItem(LocStoreKeys.FEATURE_WORK_TRIPS) === 'true' || false;
  const featureCustomFeatureForClientF =
    localStorage.getItem(LocStoreKeys.FEATURE_CUSTOM_FEATURE_FOR_CLIENT_F) === 'true' || false;
  const featureLimitWfhLeaveDays = localStorage.getItem(LocStoreKeys.FEATURE_LIMIT_WFH_LEAVE_DAYS) === 'true' || false;
  const featureShowWFHLeaveTypeInMainDropdown =
    localStorage.getItem(LocStoreKeys.FEATURE_SHOW_WFH_LEAVE_TYPE_IN_MAIN_DROPDOWN) === 'true' || false;
  const featureGroupRoleLimitsIsActive =
    localStorage.getItem(LocStoreKeys.FEATURE_GROUP_ROLE_LIMITS_IS_ACTIVE) === 'true' || false;
  const featureReminderForProbationPeriodEnd =
    localStorage.getItem(LocStoreKeys.FEATURE_REMINDER_FOR_PROBATION_PERIOD_END) === 'true' || false;
  const featureLimitDocumentsToLeaves =
    localStorage.getItem(LocStoreKeys.FEATURE_LIMIT_DOCUMENTS_TO_LEAVES) === 'true' || false;
  const reminderForIdCardExpiration =
    localStorage.getItem(LocStoreKeys.REMINDER_FOR_ID_CARD_EXPIRATION) === 'true' || false;
  const shouldDisallowLeaveDays = localStorage.getItem(LocStoreKeys.SHOULD_DISALLOW_LEAVE_DAYS) === 'true' || false;
  const disallowedLeavesFrom = localStorage.getItem(LocStoreKeys.DISALLOWED_LEAVES_FROM) || '';
  const disallowedLeavesTo = localStorage.getItem(LocStoreKeys.DISALLOWED_LEAVES_TO) || '';
  const featureLeaveSubstituteEmployee =
    localStorage.getItem(LocStoreKeys.FEATURE_LEAVE_SUBSTITUTE_EMPLOYEE) === 'true' || false;
  const featureRequireLeaveSubstituteEmployee =
    localStorage.getItem(LocStoreKeys.FEATURE_REQUIRE_LEAVE_SUBSTITUTE_EMPLOYEE) === 'true' || false;
  const featureEmployeeCandidatesIsActive =
    localStorage.getItem(LocStoreKeys.FEATURE_EMPLOYEE_CANDIDATES_IS_ACTIVE) === 'true' || false;
  const isCandidate = localStorage.getItem(LocStoreKeys.IS_CANDIDATE) === 'true' || false;
  const employeeCandidateHelpText = localStorage.getItem(LocStoreKeys.EMPLOYEE_CANDIDATE_HELP_TEXT) || '';
  const totalHourlyAbsencesRemainingHours =
    localStorage.getItem(LocStoreKeys.TOTAL_HOURLY_ABSENCES_REMAINING_HOURS) || '';
  const totalHourlyAbsencesUncoveredHours =
    localStorage.getItem(LocStoreKeys.TOTAL_HOURLY_ABSENCES_UNCOVERED_HOURS) || '';
  const totalHourlyAbsencesHoursAboutToBeCovered =
    localStorage.getItem(LocStoreKeys.TOTAL_HOURLY_ABSENCES_HOURS_ABOUT_TO_BE_COVERED) || '';
  const hasAvailableArchives = localStorage.getItem(LocStoreKeys.HAS_AVAILABLE_ARCHIVES) === 'true' || false;
  const availableUndownloadedArchivesCount =
    localStorage.getItem(LocStoreKeys.AVAILABLE_UNDOWNLOADED_ARCHIVES_COUNT) || '0';
  const inProgressArchivesCount = localStorage.getItem(LocStoreKeys.IN_PROGRESS_ARCHIVES_COUNT) || '0';
  const lastArchiveExpiresAt = localStorage.getItem(LocStoreKeys.LAST_ARCHIVE_EXPIRES_AT) || null;

  return {
    id: parseInt(id, 10),
    paidLeaveDays: parseFloat(paidLeaveDays),
    paidLeaveDaysByYear: JSON.parse(paidLeaveDaysByYear),
    paidLeaveDaysServedTime: parseFloat(paidLeaveDaysServedTime),
    paidLeaveDaysIrregularWorkingHours: parseFloat(paidLeaveDaysIrregularWorkingHours),
    paidLeaveDaysSpecificWorkingConditions: parseFloat(paidLeaveDaysSpecificWorkingConditions),
    paidLeaveDaysAdditionalLeave: parseFloat(paidLeaveDaysAdditionalLeave),
    name,
    email,
    featureLeavesIsActive,
    featureLeaveReview,
    featureLeavesHalfDayOption,
    featureForm76,
    featureColleaguesLeaves,
    featureExitInterview,
    featureSickLeaveWithoutAttachment,
    featureArt157LeaveWithoutAttachment,
    featureLeaveDaysServedTime,
    featureLeaveDaysIrregularWorkingHours,
    featureLeaveDaysSpecificWorkingConditions,
    featureLeaveDaysAdditionalLeave,
    featureHourlyAbsences,
    featureHealthAndSafetyTraining,
    featureWorkTrips,
    featureCustomFeatureForClientF,
    featureLimitWfhLeaveDays,
    featureShowWFHLeaveTypeInMainDropdown,
    featureLeaveSubstituteEmployee,
    featureRequireLeaveSubstituteEmployee,
    featureEmployeeCandidatesIsActive,
    featureGroupRoleLimitsIsActive,
    featureReminderForProbationPeriodEnd,
    featureLimitDocumentsToLeaves,
    reminderForIdCardExpiration,
    wfhLimitType,
    remainingWfhLeavesPerWeek: parseFloat(remainingWfhLeavesPerWeek),
    remainingWfhLeavesPerMonth: parseFloat(remainingWfhLeavesPerMonth),
    shouldDisallowLeaveDays,
    disallowedLeavesFrom,
    disallowedLeavesTo,
    isCandidate,
    employeeCandidateHelpText,
    totalHourlyAbsencesRemainingHours: parseFloat(totalHourlyAbsencesRemainingHours),
    totalHourlyAbsencesUncoveredHours: parseFloat(totalHourlyAbsencesUncoveredHours),
    totalHourlyAbsencesHoursAboutToBeCovered: parseFloat(totalHourlyAbsencesHoursAboutToBeCovered),
    hasAvailableArchives,
    availableUndownloadedArchivesCount: parseInt(availableUndownloadedArchivesCount, 10),
    inProgressArchivesCount: parseInt(inProgressArchivesCount, 10),
    lastArchiveExpiresAt: lastArchiveExpiresAt || null,
  };
};

export const getAccessToken = (): string | null => {
  const LocStoreKeys = getLocStoreKeys();
  return localStorage.getItem(LocStoreKeys.ACCESS_TOKEN);
};

export const getRefreshToken = (): string | null => {
  const LocStoreKeys = getLocStoreKeys();
  return localStorage.getItem(LocStoreKeys.REFRESH_TOKEN);
};

export const resetStorage = (): void => {
  // Keep Language setting after logout
  const LocStoreKeys = getLocStoreKeys();
  const lang = localStorage.getItem(LocStoreKeys.LANGUAGE);
  localStorage.clear();
  localStorage.setItem(LocStoreKeys.LANGUAGE, lang || 'bg');
};
